import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { isEmpty } from "lodash";
import Navbar from "../Navbar/Navbar";
import Footer from "../footer/Footer";
import Scrollbar from "../scrollbar/scrollbar";

const STATUS_FLOW = ["Pending", "Processing", "Dispatched", "Delivered"];

const STATUS_META = {
  Pending: {
    eyebrow: "ORDER RECEIVED",
    title: "Thank you for your",
    accent: "order",
    sub: "We've received your order. Once payment is confirmed, your gift cards will be processed and emailed to you.",
    tone: "gold",
    icon: (
      <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
  },
  Processing: {
    eyebrow: "PROCESSING",
    title: "Your order is being",
    accent: "prepared",
    sub: "Payment confirmed. We're generating your gift card codes — they'll arrive in your inbox within minutes.",
    tone: "gold",
    icon: (
      <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12a9 9 0 11-9-9" />
        <polyline points="21 4 21 12 13 12" />
      </svg>
    ),
  },
  Dispatched: {
    eyebrow: "DISPATCHED",
    title: "Your gift cards are on the",
    accent: "way",
    sub: "Your gift cards have been dispatched and are heading to your inbox right now.",
    tone: "gold",
    icon: (
      <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </svg>
    ),
  },
  Delivered: {
    eyebrow: "DELIVERED",
    title: "Your order has been",
    accent: "delivered",
    sub: "Your gift cards have been delivered to your email. Enjoy — and thank you for shopping with us.",
    tone: "green",
    icon: (
      <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  Cancelled: {
    eyebrow: "ORDER CANCELLED",
    title: "This order has been",
    accent: "cancelled",
    sub: "This order was cancelled. If you've been charged, the amount will be refunded within 5–7 business days.",
    tone: "red",
    icon: (
      <svg viewBox="0 0 24 24" width="42" height="42" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </svg>
    ),
  },
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const OrderConfirmSec = ({ carts, token, ...props }) => {
  const { orderId } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const getUserCartData = async () => {
    setLoading(true);
    if (error) setError(false);
    await axios
      .get("orders", {
        params: { orderid: orderId },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setData(res?.data?.data?.[0] || {});
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  useEffect(() => {
    getUserCartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const status = data?.orderStatus || "Pending";
  const meta = STATUS_META[status] || STATUS_META.Pending;
  const isCancelled = status === "Cancelled";

  const stepIndex = useMemo(() => {
    const idx = STATUS_FLOW.indexOf(status);
    return idx === -1 ? 0 : idx;
  }, [status]);

  const currencyPrefix =
    data?.webhook?.currency === "USD"
      ? "$ "
      : data?.webhook?.currency === "EUR"
      ? "€ "
      : "AED ";

  const itemCount = data?.orderItems?.length || 0;
  const deliveryCharge = data?.delivery_charge ?? data?.delivery_charges ?? 0;

  return (
    <>
      <Navbar />

      <section className={`gpok-section gpok-tone-${meta.tone}`}>
        <div className="gpok-bg-orb gpok-bg-orb-tl" />
        <div className="gpok-bg-orb gpok-bg-orb-br" />

        <div className="gpok-inner">
          {loading ? (
            <div className="gpok-card gpok-skeleton">
              <div className="gpok-sk-icon" />
              <div className="gpok-sk-line gpok-sk-line-lg" />
              <div className="gpok-sk-line gpok-sk-line-md" />
              <div className="gpok-sk-rows">
                {[0, 1, 2].map((i) => (
                  <div className="gpok-sk-row" key={i}>
                    <div className="gpok-sk-thumb" />
                    <div className="gpok-sk-line gpok-sk-line-sm" />
                    <div className="gpok-sk-line gpok-sk-line-xs" />
                  </div>
                ))}
              </div>
            </div>
          ) : error && isEmpty(data) ? (
            <div className="gpok-card gpok-error">
              <div className="gpok-error-icon">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h2 className="gpok-title">Unable to load order</h2>
              <p className="gpok-sub">We couldn't fetch your order details right now. Please refresh, or contact support if the issue persists.</p>
              <div className="gpok-actions">
                <button type="button" className="gpok-btn gpok-btn-primary" onClick={getUserCartData}>
                  Try Again
                </button>
                <Link to="/" className="gpok-btn gpok-btn-ghost">Back Home</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="gpok-card">
                <div className="gpok-icon-wrap">
                  <div className="gpok-icon-ring" />
                  <div className="gpok-icon-ring gpok-icon-ring-2" />
                  <div className="gpok-icon">{meta.icon}</div>
                </div>

                <div className="gpok-eyebrow">
                  <span className="gpok-eyebrow-dot" />
                  {meta.eyebrow}
                </div>

                <h1 className="gpok-title">
                  {meta.title} <span className="gpok-title-accent">{meta.accent}</span>
                </h1>
                <p className="gpok-sub">{meta.sub}</p>

                <div className="gpok-ref-row">
                  {orderId && (
                    <div className="gpok-order-ref">
                      <span className="gpok-order-ref-label">Order ID</span>
                      <span className="gpok-order-ref-id">{orderId}</span>
                    </div>
                  )}
                  {data?.createdAt && (
                    <div className="gpok-order-ref">
                      <span className="gpok-order-ref-label">Placed</span>
                      <span className="gpok-order-ref-id gpok-ref-date">{formatDateTime(data.createdAt)}</span>
                    </div>
                  )}
                </div>

                <div className="gpok-actions">
                  <Link to="/your-orders" className="gpok-btn gpok-btn-primary">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11H5a2 2 0 0 0-2 2v7h18v-7a2 2 0 0 0-2-2h-4" />
                      <path d="M12 4v12" />
                      <polyline points="8 8 12 4 16 8" />
                    </svg>
                    View My Orders
                  </Link>
                  <Link to="/shop" className="gpok-btn gpok-btn-ghost">
                    Continue Shopping
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* TIMELINE — only for non-cancelled orders */}
              {!isCancelled && (
                <div className="gpok-detail-card">
                  <div className="gpok-detail-head">
                    <h2>Order Progress</h2>
                    <span className={`gpok-status-pill gpok-status-${meta.tone}`}>{status}</span>
                  </div>

                  <div className="gpok-timeline">
                    {STATUS_FLOW.map((s, i) => {
                      const reached = i <= stepIndex;
                      const current = i === stepIndex;
                      return (
                        <div
                          key={s}
                          className={`gpok-tl-step ${reached ? "is-reached" : ""} ${current ? "is-current" : ""}`}
                        >
                          <div className="gpok-tl-dot">
                            {reached ? (
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <span>{i + 1}</span>
                            )}
                          </div>
                          <div className="gpok-tl-label">{s}</div>
                          {i < STATUS_FLOW.length - 1 && (
                            <div className={`gpok-tl-bar ${i < stepIndex ? "is-filled" : ""}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ORDER DETAILS */}
              <div className="gpok-detail-card">
                <div className="gpok-detail-head">
                  <h2>Order Details</h2>
                  <span className="gpok-pill">
                    {itemCount} {itemCount === 1 ? "item" : "items"}
                  </span>
                </div>

                <div className="gpok-items">
                  {data?.orderItems?.map((item, idx) => (
                    <div className="gpok-item" key={item.productid || idx}>
                      <div className="gpok-item-thumb">
                        {item?.proimg?.[0]?.src ? (
                          <img src={item.proimg[0].src} alt={item?.name} />
                        ) : (
                          <div className="gpok-thumb-fallback" />
                        )}
                      </div>
                      <div className="gpok-item-meta">
                        <h4>{item?.name}</h4>
                        {item?.brand && item?.brand !== item?.name && (
                          <span className="gpok-item-brand">{item.brand}</span>
                        )}
                        <span className="gpok-item-qty">
                          {currencyPrefix}{item.price} <span className="gpok-x">×</span> {item.qty}
                        </span>
                      </div>
                      <div className="gpok-item-price">
                        {currencyPrefix}{(item.qty * item.price).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="gpok-summary">
                  <div className="gpok-summary-row">
                    <span>Total Products</span>
                    <span>{itemCount}</span>
                  </div>
                  {Number(deliveryCharge) > 0 && (
                    <div className="gpok-summary-row">
                      <span>Delivery Charge</span>
                      <span>{currencyPrefix}{deliveryCharge}</span>
                    </div>
                  )}
                  <div className="gpok-summary-row gpok-summary-total">
                    <span>Total {isCancelled ? "" : data?.isPaid ? "Paid" : "Amount"}</span>
                    <span className="gpok-total-amt">
                      {currencyPrefix}{Number(data?.totalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* PAYMENT DETAILS */}
              <div className="gpok-detail-card">
                <div className="gpok-detail-head">
                  <h2>Payment Details</h2>
                  <span className={`gpok-status-pill ${
                    data?.isPaid
                      ? "gpok-status-green"
                      : data?.paymentStatus === "Failed"
                      ? "gpok-status-red"
                      : "gpok-status-gold"
                  }`}>
                    {data?.isPaid ? "Paid" : data?.paymentStatus || "Pending"}
                  </span>
                </div>

                <div className="gpok-meta-grid">
                  <div className="gpok-meta-item">
                    <span className="gpok-meta-label">Method</span>
                    <span className="gpok-meta-val">{data?.method || "—"}</span>
                  </div>
                  <div className="gpok-meta-item">
                    <span className="gpok-meta-label">Payment Status</span>
                    <span className="gpok-meta-val">{data?.paymentStatus || "Pending"}</span>
                  </div>
                  <div className="gpok-meta-item">
                    <span className="gpok-meta-label">Transaction ID</span>
                    <span className="gpok-meta-val gpok-meta-mono">{data?.transactionId || "—"}</span>
                  </div>
                  <div className="gpok-meta-item">
                    <span className="gpok-meta-label">Paid At</span>
                    <span className="gpok-meta-val">{formatDateTime(data?.paidAt)}</span>
                  </div>
                </div>

                {data?.message && (
                  <div className="gpok-message">
                    <strong>Note:</strong> {data.message}
                  </div>
                )}
              </div>

              {/* SUPPORT STRIP */}
              <div className="gpok-support">
                <div className="gpok-support-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.94 11.94 0 0012.06 0C5.5 0 .19 5.31.19 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.34-1.66a11.85 11.85 0 005.71 1.45h.01c6.55 0 11.86-5.31 11.86-11.86 0-3.17-1.23-6.15-3.4-8.45zM12.06 21.7h-.01a9.85 9.85 0 01-5.02-1.37l-.36-.21-3.76.98 1-3.66-.23-.38a9.83 9.83 0 01-1.51-5.2c0-5.45 4.43-9.88 9.88-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 012.89 6.99c0 5.45-4.43 9.83-9.87 9.83z" />
                  </svg>
                </div>
                <div className="gpok-support-body">
                  <h4>{isCancelled ? "Need help with a refund?" : "Didn't receive your codes?"}</h4>
                  <p>
                    {isCancelled
                      ? "Our team will help track your refund and answer any questions."
                      : "If your gift cards haven't arrived in your inbox within a few minutes, our team can resend them instantly."}
                  </p>
                </div>
                <a
                  href={`https://wa.me/971545740174?text=${encodeURIComponent(
                    `Hello GlobalGiftPass, I need help with my order ${orderId || ""}.`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="gpok-wa-btn"
                >
                  Get Help
                </a>
              </div>
            </>
          )}
        </div>

        <style>{`
          .gpok-section {
            position: relative;
            background: linear-gradient(180deg, #07060a 0%, #0b0b0c 100%);
            color: #e6e8ec;
            padding: 60px 22px 90px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            overflow: hidden;
            min-height: 100vh;
          }
          .gpok-bg-orb {
            position: absolute;
            width: 460px;
            height: 460px;
            border-radius: 50%;
            filter: blur(140px);
            opacity: 0.4;
            pointer-events: none;
          }
          .gpok-tone-green .gpok-bg-orb-tl { background: rgba(74, 222, 128, 0.18); top: -180px; left: -120px; }
          .gpok-tone-gold  .gpok-bg-orb-tl { background: rgba(212,168,87,0.22);   top: -180px; left: -120px; }
          .gpok-tone-red   .gpok-bg-orb-tl { background: rgba(255, 90, 90, 0.18); top: -180px; left: -120px; }
          .gpok-bg-orb-br { background: rgba(212,168,87,0.16); bottom: -200px; right: -120px; }

          .gpok-inner {
            position: relative;
            max-width: 760px;
            width: 100%;
            margin: 0 auto;
            z-index: 1;
            display: flex;
            flex-direction: column;
            gap: 18px;
          }

          /* MAIN HERO CARD */
          .gpok-card {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 22px;
            padding: 50px 36px 40px;
            text-align: center;
            box-shadow: 0 18px 40px rgba(0,0,0,0.4);
          }

          .gpok-icon-wrap {
            position: relative;
            width: 110px;
            height: 110px;
            margin: 0 auto 22px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .gpok-icon-ring {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            border: 2px solid rgba(212,168,87,0.3);
            animation: gpok-ring 2.4s ease-out infinite;
          }
          .gpok-tone-green .gpok-icon-ring { border-color: rgba(74, 222, 128, 0.3); }
          .gpok-tone-red   .gpok-icon-ring { border-color: rgba(255, 90, 90, 0.3); }
          .gpok-icon-ring-2 { animation-delay: 1.2s; }
          @keyframes gpok-ring {
            0% { transform: scale(0.85); opacity: 0.85; }
            100% { transform: scale(1.4); opacity: 0; }
          }
          .gpok-icon {
            position: relative;
            width: 84px;
            height: 84px;
            border-radius: 50%;
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: inset 0 -4px 12px rgba(0,0,0,0.18);
            animation: gpok-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          }
          .gpok-tone-gold  .gpok-icon { background: linear-gradient(135deg, #f1cf85, #b58a3f); color: #0b0b0c; box-shadow: 0 12px 28px rgba(212,168,87,0.35), inset 0 -4px 12px rgba(0,0,0,0.18); }
          .gpok-tone-green .gpok-icon { background: linear-gradient(135deg, #4ade80, #16a34a); box-shadow: 0 12px 28px rgba(22, 163, 74, 0.4), inset 0 -4px 12px rgba(0,0,0,0.18); }
          .gpok-tone-red   .gpok-icon { background: linear-gradient(135deg, #ff6b6b, #c0392b); box-shadow: 0 12px 28px rgba(192, 57, 43, 0.45), inset 0 -4px 12px rgba(0,0,0,0.18); }
          @keyframes gpok-pop {
            0% { transform: scale(0); }
            100% { transform: scale(1); }
          }

          .gpok-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 14px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 2px;
            margin-bottom: 14px;
          }
          .gpok-tone-gold .gpok-eyebrow  { background: rgba(212,168,87,0.1);  border: 1px solid rgba(212,168,87,0.3);  color: #d4a857; }
          .gpok-tone-green .gpok-eyebrow { background: rgba(74, 222, 128, 0.1); border: 1px solid rgba(74, 222, 128, 0.3); color: #4ade80; }
          .gpok-tone-red .gpok-eyebrow   { background: rgba(255, 90, 90, 0.1); border: 1px solid rgba(255, 90, 90, 0.3); color: #ff7a7a; }
          .gpok-eyebrow-dot {
            width: 6px; height: 6px; border-radius: 50%;
            animation: gpok-pulse 1.6s ease-in-out infinite;
          }
          .gpok-tone-gold .gpok-eyebrow-dot  { background: #d4a857; box-shadow: 0 0 10px #d4a857; }
          .gpok-tone-green .gpok-eyebrow-dot { background: #4ade80; box-shadow: 0 0 10px #4ade80; }
          .gpok-tone-red .gpok-eyebrow-dot   { background: #ff7a7a; box-shadow: 0 0 10px #ff7a7a; }
          @keyframes gpok-pulse {
            0%,100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.55; transform: scale(0.85); }
          }

          .gpok-title {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 34px;
            color: #fff;
            font-weight: 700;
            margin: 0 0 12px;
            line-height: 1.2;
          }
          .gpok-title-accent {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            font-style: italic;
            font-weight: 600;
          }
          .gpok-tone-red .gpok-title-accent { background: linear-gradient(135deg, #ff8a8a, #c0392b); -webkit-background-clip: text; background-clip: text; color: transparent; }
          .gpok-sub {
            font-size: 14.5px;
            color: #9aa0a8;
            line-height: 1.65;
            max-width: 540px;
            margin: 0 auto 22px;
          }

          .gpok-ref-row {
            display: flex;
            gap: 10px;
            justify-content: center;
            flex-wrap: wrap;
            margin-bottom: 22px;
          }
          .gpok-order-ref {
            display: inline-flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            background: rgba(255,255,255,0.03);
            border: 1px dashed rgba(212,168,87,0.25);
            border-radius: 12px;
          }
          .gpok-order-ref-label {
            font-size: 11px;
            color: #9aa0a8;
            text-transform: uppercase;
            letter-spacing: 1.4px;
            font-weight: 700;
          }
          .gpok-order-ref-id {
            font-size: 13.5px;
            color: #d4a857;
            font-weight: 700;
            font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
          }
          .gpok-ref-date { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 13px; color: #fff; }

          .gpok-actions {
            display: flex;
            gap: 12px;
            justify-content: center;
            flex-wrap: wrap;
          }
          .gpok-btn {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 13px 26px;
            border-radius: 12px;
            font-size: 13px;
            font-weight: 800;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            cursor: pointer;
            text-decoration: none;
            border: none;
            transition: transform .2s ease, box-shadow .25s ease, background .2s ease, gap .2s ease;
          }
          .gpok-btn-primary {
            background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
            color: #0b0b0c;
          }
          .gpok-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 14px 30px rgba(212,168,87,0.35);
            color: #0b0b0c;
            text-decoration: none;
          }
          .gpok-btn-ghost {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(212,168,87,0.3);
            color: #d4a857;
          }
          .gpok-btn-ghost:hover {
            background: rgba(212,168,87,0.1);
            border-color: rgba(212,168,87,0.55);
            color: #f1cf85;
            text-decoration: none;
            transform: translateY(-2px);
            gap: 12px;
          }

          /* DETAIL CARD */
          .gpok-detail-card {
            background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
            border: 1px solid rgba(212,168,87,0.22);
            border-radius: 18px;
            padding: 26px;
            box-shadow: 0 12px 30px rgba(0,0,0,0.35);
          }
          .gpok-detail-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 18px;
            padding-bottom: 14px;
            border-bottom: 1px solid rgba(212,168,87,0.15);
          }
          .gpok-detail-head h2 {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 20px;
            color: #fff;
            margin: 0;
            font-weight: 700;
          }
          .gpok-pill {
            padding: 5px 12px;
            background: rgba(212,168,87,0.12);
            border: 1px solid rgba(212,168,87,0.28);
            color: #d4a857;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .gpok-status-pill {
            padding: 5px 12px;
            border-radius: 999px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 1px;
            text-transform: uppercase;
          }
          .gpok-status-gold  { background: rgba(212,168,87,0.12);  border: 1px solid rgba(212,168,87,0.32);  color: #d4a857; }
          .gpok-status-green { background: rgba(74, 222, 128, 0.12); border: 1px solid rgba(74, 222, 128, 0.32); color: #4ade80; }
          .gpok-status-red   { background: rgba(255, 90, 90, 0.12); border: 1px solid rgba(255, 90, 90, 0.32); color: #ff7a7a; }

          /* TIMELINE */
          .gpok-timeline {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            position: relative;
            padding: 6px 0 4px;
          }
          .gpok-tl-step {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            min-width: 0;
          }
          .gpok-tl-dot {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: rgba(255,255,255,0.04);
            border: 1.5px solid rgba(255,255,255,0.12);
            color: #6e7480;
            font-size: 12px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            z-index: 2;
            transition: all .25s ease;
          }
          .gpok-tl-step.is-reached .gpok-tl-dot {
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            border-color: #d4a857;
            color: #0b0b0c;
          }
          .gpok-tl-step.is-current .gpok-tl-dot {
            box-shadow: 0 0 0 4px rgba(212,168,87,0.18), 0 8px 18px rgba(212,168,87,0.25);
            transform: scale(1.08);
          }
          .gpok-tl-label {
            margin-top: 10px;
            font-size: 11.5px;
            font-weight: 700;
            letter-spacing: 0.6px;
            text-transform: uppercase;
            color: #6e7480;
            text-align: center;
          }
          .gpok-tl-step.is-reached .gpok-tl-label { color: #d4a857; }
          .gpok-tl-step.is-current .gpok-tl-label { color: #fff; }
          .gpok-tl-bar {
            position: absolute;
            top: 16px;
            left: 50%;
            right: -50%;
            height: 2px;
            background: rgba(255,255,255,0.1);
            z-index: 1;
          }
          .gpok-tl-bar.is-filled { background: linear-gradient(90deg, #d4a857, #b58a3f); }

          /* ITEMS */
          .gpok-items { display: flex; flex-direction: column; gap: 10px; margin-bottom: 18px; }
          .gpok-item {
            display: grid;
            grid-template-columns: 56px 1fr auto;
            gap: 14px;
            align-items: center;
            padding: 12px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 12px;
            transition: border-color .25s ease, background .25s ease;
          }
          .gpok-item:hover {
            border-color: rgba(212,168,87,0.3);
            background: rgba(212,168,87,0.04);
          }
          .gpok-item-thumb {
            width: 56px;
            height: 56px;
            border-radius: 10px;
            overflow: hidden;
            background: #0b0b0c;
            border: 1px solid rgba(212,168,87,0.2);
          }
          .gpok-item-thumb img { width: 100%; height: 100%; object-fit: cover; }
          .gpok-thumb-fallback {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #1a1208, #0b0b0c);
          }
          .gpok-item-meta { min-width: 0; text-align: left; }
          .gpok-item-meta h4 {
            font-size: 14.5px;
            color: #fff;
            margin: 0 0 4px;
            font-weight: 600;
            line-height: 1.3;
          }
          .gpok-item-brand {
            display: block;
            font-size: 11.5px;
            color: #d4a857;
            font-weight: 600;
            letter-spacing: 0.4px;
            margin-bottom: 4px;
          }
          .gpok-item-qty { font-size: 12.5px; color: #9aa0a8; }
          .gpok-x { color: #d4a857; font-weight: 700; margin: 0 4px; }
          .gpok-item-price {
            font-size: 15px;
            color: #d4a857;
            font-weight: 700;
            white-space: nowrap;
          }

          .gpok-summary {
            padding-top: 16px;
            border-top: 1px dashed rgba(212,168,87,0.18);
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .gpok-summary-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            color: #c5c8cf;
          }
          .gpok-summary-row span:last-child { color: #fff; font-weight: 600; }
          .gpok-summary-total {
            margin-top: 8px;
            padding-top: 14px;
            border-top: 1px solid rgba(212,168,87,0.22);
            font-size: 16px;
          }
          .gpok-summary-total span:first-child {
            font-family: "Playfair Display", "Times New Roman", serif;
            color: #fff;
            font-weight: 700;
            font-size: 18px;
          }
          .gpok-total-amt {
            font-size: 22px !important;
            background: linear-gradient(135deg, #f1cf85, #b58a3f);
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent !important;
            font-weight: 700 !important;
          }

          /* META GRID (payment details) */
          .gpok-meta-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .gpok-meta-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 12px 14px;
            background: rgba(255,255,255,0.02);
            border: 1px solid rgba(255,255,255,0.06);
            border-radius: 12px;
          }
          .gpok-meta-label {
            font-size: 10.5px;
            color: #6e7480;
            text-transform: uppercase;
            letter-spacing: 1.4px;
            font-weight: 700;
          }
          .gpok-meta-val {
            font-size: 13.5px;
            color: #fff;
            font-weight: 600;
            word-break: break-all;
          }
          .gpok-meta-mono {
            font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
            color: #d4a857;
            font-size: 12.5px;
          }
          .gpok-message {
            margin-top: 14px;
            padding: 12px 14px;
            background: rgba(212,168,87,0.08);
            border: 1px solid rgba(212,168,87,0.2);
            border-radius: 10px;
            font-size: 13px;
            color: #c5c8cf;
            line-height: 1.5;
          }
          .gpok-message strong { color: #d4a857; }

          /* SUPPORT STRIP */
          .gpok-support {
            display: grid;
            grid-template-columns: 44px 1fr auto;
            gap: 14px;
            align-items: center;
            padding: 18px 22px;
            background: linear-gradient(180deg, rgba(21,17,10,0.85) 0%, rgba(26,18,8,0.85) 100%);
            border: 1px solid rgba(212,168,87,0.18);
            border-radius: 18px;
          }
          .gpok-support-icon {
            width: 44px;
            height: 44px;
            background: linear-gradient(135deg, #25d366, #128c7e);
            color: #fff;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .gpok-support-body h4 {
            font-family: "Playfair Display", "Times New Roman", serif;
            font-size: 15px;
            color: #fff;
            margin: 0 0 4px;
            font-weight: 700;
          }
          .gpok-support-body p {
            font-size: 13px;
            color: #9aa0a8;
            line-height: 1.5;
            margin: 0;
          }
          .gpok-wa-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 10px 18px;
            background: linear-gradient(135deg, #25d366, #128c7e);
            color: #fff;
            font-size: 12.5px;
            font-weight: 700;
            border-radius: 10px;
            text-decoration: none;
            white-space: nowrap;
            transition: transform .2s ease, box-shadow .25s ease;
          }
          .gpok-wa-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 22px rgba(37,211,102,0.3);
            color: #fff;
            text-decoration: none;
          }

          /* ERROR */
          .gpok-error { padding: 60px 36px; }
          .gpok-error-icon {
            width: 84px;
            height: 84px;
            margin: 0 auto 18px;
            background: rgba(255, 90, 90, 0.1);
            border: 1px solid rgba(255, 90, 90, 0.3);
            color: #ff7a7a;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          /* SKELETON */
          .gpok-skeleton { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 60px 36px; }
          .gpok-sk-icon { width: 84px; height: 84px; border-radius: 50%; background: rgba(255,255,255,0.05); animation: gpok-shimmer 1.4s ease infinite; }
          .gpok-sk-line { height: 14px; border-radius: 7px; background: rgba(255,255,255,0.05); animation: gpok-shimmer 1.4s ease infinite; }
          .gpok-sk-line-lg { width: 60%; height: 22px; }
          .gpok-sk-line-md { width: 80%; }
          .gpok-sk-line-sm { width: 70%; }
          .gpok-sk-line-xs { width: 30%; height: 10px; }
          .gpok-sk-rows { width: 100%; display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
          .gpok-sk-row {
            display: grid;
            grid-template-columns: 56px 1fr 80px;
            gap: 14px;
            align-items: center;
            padding: 12px;
            background: rgba(255,255,255,0.02);
            border-radius: 12px;
          }
          .gpok-sk-thumb { width: 56px; height: 56px; border-radius: 10px; background: rgba(255,255,255,0.05); animation: gpok-shimmer 1.4s ease infinite; }
          @keyframes gpok-shimmer {
            0%,100% { opacity: 0.5; }
            50% { opacity: 1; }
          }

          @media (max-width: 720px) {
            .gpok-support { grid-template-columns: 44px 1fr; }
            .gpok-wa-btn { grid-column: 1 / -1; justify-content: center; }
            .gpok-meta-grid { grid-template-columns: 1fr; }
          }
          @media (max-width: 575px) {
            .gpok-section { padding: 40px 14px 70px; }
            .gpok-card { padding: 40px 22px 32px; }
            .gpok-detail-card { padding: 20px; }
            .gpok-title { font-size: 26px; }
            .gpok-actions { flex-direction: column; }
            .gpok-btn { width: 100%; justify-content: center; }
            .gpok-icon-wrap { width: 96px; height: 96px; }
            .gpok-icon { width: 72px; height: 72px; }
            .gpok-tl-label { font-size: 10px; }
            .gpok-tl-dot { width: 28px; height: 28px; font-size: 11px; }
            .gpok-tl-bar { top: 14px; }
          }
        `}</style>
      </section>

      <Footer />
      <Scrollbar />
    </>
  );
};

export default OrderConfirmSec;
